import { useCallback, useState } from 'react';
import { batch } from 'react-redux';

import * as St from './StoresList.styles';

import { useEquivalentProductMatching } from '~/core/hooks/useEquivalentProductMatching/useEquivalentProductMatching';
import { useStoresContext } from '~/legacy/easy-estimator-page/context/storesContext';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { updateIsNewEstimate } from '~/redux/slices/estimate';
import { selectZipcode, updateZipcode } from '~/redux/slices/zipcode';
import LandingPageImage from '~atoms/LandingPageImage';
import RetailStores from '~organisms/RetailStores';
import RetailStoresMap from '~organisms/RetailStoresMap';
import StoreChangeModal from '~organisms/StoreChangeModal';
import StoreSearch from '~templates/StoreSearch';

type StoresListProps = {
  showZipCodeInput?: boolean;
  showStoresList?: boolean;
  disabled?: boolean;
  isZipDisabled?: boolean;
};

const StoresList = ({
  showZipCodeInput = true,
  showStoresList = true,
  disabled = false,
  isZipDisabled = false,
}: StoresListProps) => {
  const dispatch = useAppDispatch();
  const zipCode = useAppSelector(selectZipcode);
  const [isZipModalVisible, setIsZipModalVisible] = useState(false);
  const [newZipCode, setNewZipCode] = useState<string>();
  const [zipFieldResetSignal, setZipFieldResetSignal] = useState(0);

  const { replaceProductsWithEquivalents } = useEquivalentProductMatching();

  const {
    getters: { googleMapsApiKey, googleMapsMapId },
  } = useStoresContext();

  const closeZipModal = useCallback(() => {
    setIsZipModalVisible(false);
  }, []);

  const updateZipCodeAndReplaceProducts = useCallback(async () => {
    if (newZipCode) {
      const result = await replaceProductsWithEquivalents({
        zipCode: newZipCode,
      });

      if (!result) {
        setZipFieldResetSignal((signal) => signal + 1);
        closeZipModal();

        return;
      }

      dispatch(updateZipcode(newZipCode));
    }

    closeZipModal();
  }, [closeZipModal, dispatch, newZipCode, replaceProductsWithEquivalents]);

  const showMap = Boolean(googleMapsApiKey) && Boolean(googleMapsMapId);

  const isLandingState = showStoresList && !zipCode;

  return (
    <St.Container data-testid="stores-list">
      <St.LandingSection $twoColumn={isLandingState}>
        <St.SearchColumn $twoColumn={isLandingState}>
          <StoreSearch
            showZipCodeInput={showZipCodeInput}
            disabled={disabled}
            isZipDisabled={isZipDisabled}
            zipFieldResetSignal={zipFieldResetSignal}
            onSubmitWithFilledDrawingArea={(newZipcode) => {
              setNewZipCode(newZipcode);
              setIsZipModalVisible(true);
            }}
            onSubmitWithEmptyDrawingArea={async (newZipcode) => {
              const result = await replaceProductsWithEquivalents({
                zipCode: newZipcode,
              });

              if (!result) {
                setZipFieldResetSignal((signal) => signal + 1);

                return;
              }

              batch(() => {
                dispatch(updateZipcode(newZipcode));
                dispatch(updateIsNewEstimate(true));
              });
            }}
          />
        </St.SearchColumn>
        {isLandingState && (
          <St.HeroColumn>
            <LandingPageImage />
          </St.HeroColumn>
        )}
      </St.LandingSection>
      {showStoresList && <RetailStores disabled={disabled} />}
      {showStoresList && showMap && zipCode && (
        <St.MapContainer>
          <RetailStoresMap disabled={disabled} />
        </St.MapContainer>
      )}
      <StoreChangeModal
        newZipCode={newZipCode}
        isVisible={isZipModalVisible}
        handleClose={closeZipModal}
        handleChange={updateZipCodeAndReplaceProducts}
      />
    </St.Container>
  );
};

export default StoresList;
